import React, { useMemo } from "react";
import { Avatar, Box, Chip, Paper, Skeleton, Stack, Typography } from "@mui/material";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";

import Contracts from "../../Contracts";

const normalizeStage = (status) => String(status || "").trim().toLowerCase();

const EngagementMetric = ({ icon, label, value, caption, loading }) => (
  <Box
    sx={{
      p: 2,
      borderRight: { md: (theme) => `1px solid ${theme.palette.divider}` },
      "&:last-of-type": { borderRight: 0 },
    }}
  >
    <Stack direction="row" spacing={1.25} alignItems="center">
      <Avatar
        variant="rounded"
        sx={{
          width: 38,
          height: 38,
          bgcolor: "rgba(25, 118, 210, 0.08)",
          color: "primary.main",
        }}
      >
        {icon}
      </Avatar>
      <Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 800, letterSpacing: 0.35, textTransform: "uppercase" }}
        >
          {label}
        </Typography>
        {loading ? (
          <Skeleton width={72} height={36} />
        ) : (
          <Typography variant="h4" sx={{ fontWeight: 850, lineHeight: 1.15 }}>
            {value}
          </Typography>
        )}
      </Box>
    </Stack>
    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
      {caption}
    </Typography>
  </Box>
);

const EngagementsSection = ({
  busyRateRequestIds,
  contracts,
  loading,
  onGenerateMatches,
  onRequestInterview,
  onRecordInterviewOutcome,
  onSelectContractor,
  onSelectInterviewSlot,
  onResolveRateChange,
  onSubmitPosition,
  onUpdateContract,
  onWithdrawContract,
  rateChangeAuditRequests,
  rateChangeRequests,
  scheduledRateChanges,
}) => {
  const metrics = useMemo(() => {
    const all = Array.isArray(contracts) ? contracts : [];
    const active = all.filter((item) => ["active", "signed", "live"].includes(normalizeStage(item.status))).length;
    const inReview = all.filter((item) =>
      ["submitted", "shortlisted", "matched", "signed"].includes(normalizeStage(item.status))
    ).length;

    return {
      total: all.length,
      active,
      inReview,
    };
  }, [contracts]);

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ p: 2 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 850 }}>
                Engagements
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Review active work, hire requests, and pricing changes in one place.
              </Typography>
            </Box>
            <Chip
              label={loading ? "Loading" : `${metrics.total} total`}
              color={metrics.active > 0 ? "success" : "default"}
              variant={metrics.active > 0 ? "filled" : "outlined"}
              sx={{ borderRadius: 1 }}
            />
          </Stack>
        </Box>
        <Box
          sx={{
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
          }}
        >
          <EngagementMetric
            loading={loading}
            icon={<AssignmentTurnedInOutlinedIcon fontSize="small" />}
            label="Total"
            value={metrics.total}
            caption="All hire requests and engagements"
          />
          <EngagementMetric
            loading={loading}
            icon={<HandshakeOutlinedIcon fontSize="small" />}
            label="Active"
            value={metrics.active}
            caption="Current approved engagements"
          />
          <EngagementMetric
            loading={loading}
            icon={<PendingActionsOutlinedIcon fontSize="small" />}
            label="Open requests"
            value={metrics.inReview}
            caption="Submitted through signed"
          />
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Contracts
          embedded
          loading={loading}
          contracts={contracts}
          rateChangeRequests={rateChangeRequests}
          scheduledRateChanges={scheduledRateChanges}
          rateChangeAuditRequests={rateChangeAuditRequests}
          busyRateRequestIds={busyRateRequestIds}
          onGenerateMatches={onGenerateMatches}
          onRequestInterview={onRequestInterview}
          onRecordInterviewOutcome={onRecordInterviewOutcome}
          onSelectContractor={onSelectContractor}
          onSelectInterviewSlot={onSelectInterviewSlot}
          onResolveRateChange={onResolveRateChange}
          onSubmitPosition={onSubmitPosition}
          onUpdateContract={onUpdateContract}
          onWithdrawContract={onWithdrawContract}
          saving={false}
        />
      </Paper>
    </Stack>
  );
};

export default EngagementsSection;
